import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const API_BASE = `${BACKEND_URL}/api`;
console.log("API Base URL:", API_BASE);

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  timeout: 0, // No timeout by default, let individual requests decide or wait indefinitely
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    // Log timeout errors for debugging
    if (error.code === 'ECONNABORTED') {
      console.warn('[API] Request timed out:', error.config?.url);
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
  updateProfile: (data) => api.put('/auth/profile', data),
};

// Analyze APIs
export const analyzeAPI = {
  analyze: (url, forceRefresh = false) => api.post('/analyze', { url, force_refresh: forceRefresh }, { timeout: 3600000 }), // 1 hour
  getHistory: () => api.get('/analyze/history'),
  cancel: (linkId) => api.post('/analyze', { action: 'stop', link_id: linkId, url: '' }),
};

// Hotface APIs
export const hotfaceAPI = {
  save: (linkId, title) => api.post('/hotface/save', { link_id: linkId, title }),
  getList: () => api.get('/hotface'),
  delete: (id) => api.delete(`/hotface/${id}`),
};

// Notification APIs
export const notificationAPI = {
  updateSettings: (data) => api.put('/notifications/settings', data),
  send: (hotfaceIds, channels) => api.post('/notifications/send', { hotface_ids: hotfaceIds, channels }),
  getLogs: () => api.get('/notifications/logs'),
};

// Billing APIs
export const billingAPI = {
  deposit: (amount, paymentMethod = 'mock') => api.post('/billing/deposit', { amount, payment_method: paymentMethod }),
  getTransactions: () => api.get('/billing/transactions'),
  getBalance: () => api.get('/billing/balance'),
  getSettings: () => api.get('/billing/settings'),
};

// Dashboard APIs
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
};

// Admin APIs
export const adminAPI = {
  getUsers: () => api.get('/admin/users'),
  updateUser: (userId, data) => api.put(`/admin/users/${userId}`, data),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  getStats: () => api.get('/admin/stats'),
  getLogs: () => api.get('/admin/logs'),
  // Cookie management
  getCookies: () => api.get('/admin/cookies'),
  addCookie: (name, cookieString) => api.post('/admin/cookies', { name, cookie_string: cookieString }),
  bulkAddCookies: (namePrefix, cookieStrings, gender = 'both') => 
    api.post('/admin/cookies/bulk', { name_prefix: namePrefix, cookie_strings: cookieStrings, gender }),
  checkSingleCookie: (cookieString) => api.post('/admin/cookies/check-single', { cookie_string: cookieString }),
  checkAllCookies: () => api.post('/admin/cookies/check-all'),
  deleteCookie: (id) => api.delete(`/admin/cookies/${id}`),
  updateCookieStatus: (id, status) => api.put(`/admin/cookies/${id}/status?status=${status}`),
  // Evaluation criteria
  getCriteria: () => api.get('/admin/criteria'),
  updateCriteria: (data) => api.put('/admin/criteria', data),
  // Admin watchlist
  getWatchlist: () => api.get('/admin/watchlist'),
  addWatchlist: (url, name, type = 'page') => api.post('/admin/watchlist', { url, name, type }),
  deleteWatchlist: (id) => api.delete(`/admin/watchlist/${id}`),
  // Auto search settings
  getAutoSearchSettings: () => api.get('/auto-search/settings'),
  updateAutoSearchSettings: (data) => api.put('/admin/auto-search/settings', data),
  getDailySearchStats: () => api.get('/admin/auto-search/daily-stats'),
  compensateUser: (user_id, amount) => api.post(`/admin/auto-search/compensate?user_id=${user_id}&amount=${amount}`),
  // Admin Source Pool
  getSourcePool: () => api.get('/admin/source-pool'),
  addSourceItem: (url, gender, note) => api.post('/admin/source-pool', { url, gender, note }),
  importSourcePool: (urls, gender = 'both', note = '') => api.post('/admin/source-pool/import', { urls, gender, note }),
  deleteSourceItem: (id) => api.delete(`/admin/source-pool/${id}`),
  resetSourcePoolUsage: () => api.post('/admin/source-pool/reset-usage'),
  cleanupDuplicates: () => api.post('/admin/source-pool/cleanup-duplicates'),
  checkSourceStatus: () => api.post('/admin/source-pool/check-status'),
  addFromAutoResult: (url, gender = 'both', note = 'Added from results') => 
    api.post('/admin/source-pool/add-from-result', { url, gender, note }),
  // AI Settings
  getAISettings: () => api.get('/admin/ai-settings'),
  updateAISettings: (data) => api.put('/admin/ai-settings', data),
  deleteAIKey: (index, provider = 'gemini') => api.delete(`/admin/ai-settings/keys/${index}?provider=${provider}`),
  resetAISettingsStatus: () => api.post('/admin/ai-settings/reset-status'),
  // Character mapping
  getCharMapping: () => api.get('/admin/char-mapping'),
  updateCharMapping: (mappingText) => api.put('/admin/char-mapping', { mapping_text: mappingText }),
  exportCharMapping: () => api.get('/admin/char-mapping/export'),
  // Site settings
  getSiteSettings: () => api.get('/site-settings'),
  updateSiteSettings: (data) => api.put('/admin/site-settings', data),
  // Announcements
  getAnnouncements: () => api.get('/admin/announcements'),
  createAnnouncement: (data) => api.post('/admin/announcements', data),
  updateAnnouncement: (id, data) => api.put(`/admin/announcements/${id}`, data),
  deleteAnnouncement: (id) => api.delete(`/admin/announcements/${id}`),
  // System health
  getSystemHealth: () => api.get('/admin/system-health'),
  // Market trend
  getMarketTrendSettings: () => api.get('/admin/market-trend/settings'),
  updateMarketTrendSettings: (data) => api.put('/admin/market-trend/settings', data),
  runMarketTrend: () => api.post('/admin/market-trend/run', {}, { timeout: 3600000 }), // 1 hour
  getLatestMarketTrend: () => api.get('/admin/market-trend/latest'),
  editLatestMarketTrend: (data) => api.put('/admin/market-trend/latest/edit', data),

  // Bypass Config
  getBypasses: () => api.get('/admin/bypass-config'),
  // Feature flags
  getFeatureFlags: () => api.get('/admin/feature-flags'),
  updateFeatureFlag: (id, data) => api.put(`/admin/feature-flags/${id}`, data),
  topupUser: (userId, data) => api.post(`/admin/users/${userId}/topup`, data),
  cancelVip: (userId) => api.post(`/admin/users/${userId}/cancel-vip`),
  // VIP Config
  getVipConfig: () => api.get('/admin/vip-config'),
  updateVipConfig: (data) => api.post('/admin/vip-config', data),
  // Data maintenance
  getMaintenanceStats: () => api.get('/admin/maintenance/stats'),
  cleanupData: (data) => api.post('/admin/maintenance/cleanup', data),
  getAnalyzedLinks: () => api.get('/admin/analyzed-links'),
  // Discovery Results Management
  getDiscoveryResults: () => api.get('/admin/auto-search/results'),
  deleteDiscoveryResult: (id) => api.delete(`/admin/auto-search/results/${id}`),
  runDiscoveryScan: (data) => api.post('/auto-search/run', data, { timeout: 3600000 }),
  // Detailed Maintenance
  getUserUsageStats: () => api.get('/admin/maintenance/user-usage'),
  resetUserData: (userId, collections) => api.post(`/admin/maintenance/user-reset/${userId}`, { collections }),
  // System Action Logs
  getSystemLogs: () => api.get('/admin/system-logs'),
  clearSystemLogs: () => api.delete('/admin/system-logs'),
  // Livestreaming
  getStreams: () => api.get('/admin/streams'),
  startStream: (data) => api.post('/admin/streams/start', data),
  stopStream: (streamId) => api.post(`/admin/streams/${streamId}/stop`),
  deleteStream: (streamId) => api.delete(`/admin/streams/${streamId}`),
  getStreamLogs: (streamId) => api.get(`/admin/streams/${streamId}/logs`),
  uploadStreamVideo: (formData) => api.post('/admin/streams/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  // Analyze Requests
  getAnalyzeRequests: () => api.get('/admin/analyze-requests'),
  approveAnalyzeRequest: (id, data) => api.post(`/admin/analyze-requests/${id}/approve`, data),
  rejectAnalyzeRequest: (id) => api.post(`/admin/analyze-requests/${id}/reject`),
};

// User Watchlist APIs
export const watchlistAPI = {
  getList: () => api.get('/watchlist'),
  add: (url, name, type = 'page') => api.post('/watchlist', { url, name, type }),
  delete: (id) => api.delete(`/watchlist/${id}`),
};

// Auto Search APIs
export const autoSearchAPI = {
  getSchedule: () => api.get('/auto-search/schedule'),
  createSchedule: (time, enabled = true, config = {}) => api.post('/auto-search/schedule', { time, enabled, ...config }),
  updateSchedule: (time, enabled, config = {}) => api.put('/auto-search/schedule', { time, enabled, ...config }),
  deleteSchedule: () => api.delete('/auto-search/schedule'),
  getResults: () => api.get('/auto-search/results'),
  getLogs: () => api.get('/auto-search/logs'),
  runNow: (data) => api.post('/auto-search/run', data, { timeout: 3600000 }),
  deleteResult: (id) => api.delete(`/auto-search/results/${id}`),
  clearHistory: () => api.post('/auto-search/results/clear'),
};

// AI Content APIs
export const aiContentAPI = {
  generate: (prompt, contentType = 'post', tone = 'friendly', applyBypass = false) => 
    api.post('/ai-content/generate', { prompt, content_type: contentType, tone, apply_bypass: applyBypass }),
  getHistory: () => api.get('/ai-content/history'),
  bypassText: (text) => api.post('/ai-content/bypass', { text }),
};

// Public APIs (no auth required for site-settings)
export const publicAPI = {
  getSiteSettings: () => axios.get(`${API_BASE}/site-settings`),
};

// Announcement APIs (user-facing)
export const announcementAPI = {
  getActive: () => api.get('/announcements'),
};

// Feature Flags APIs (user-facing)
export const featureFlagsAPI = {
  getAll: () => api.get('/feature-flags'),
};

// Tutorial APIs
export const tutorialsAPI = {
  getAll: () => api.get('/tutorials'),
  adminGetAll: () => api.get('/admin/tutorials'),
  adminSave: (data) => api.post('/admin/tutorials', data),
  adminDelete: (id) => api.delete(`/admin/tutorials/${id}`),
};

export default api;
